import React, { Fragment } from 'react';
// Contexts
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faScrewdriver } from '@fortawesome/free-solid-svg-icons';
import { LinearProgress } from '@mui/material';
import { withRouter } from 'react-router-dom';
import { Row, Col } from 'reactstrap';
import { Can } from '../Can';
import CommonContext, { ApiRoutes } from '../Common';
import { StatusForm } from '../common/Generic Status/StatusForm';
import { ApplicationPaths } from '../api-authorization/ApiAuthorizationConstants';
import {
    createDataSource, createGridOptions, DataGrid, indexCellRenderer,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import {
    FlexRow, FlexStartRow, FlexEndRow, PageHeading, SmallButton,
} from '../common/forms/FormElements';
import { util } from '../Util';
import { ChargeTypeUnit } from './ChargeTypeUnit';

class ChargeTypeUnitIndex extends React.Component {
    static contextType = CommonContext;

    // #region [ Constructor and Overrides ]

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            loading: true,
            chargeTypeUnit: null,
            readOnly: true,
            formValidated: false,
            errors: {},
            showChargeTypeUnitForm: false,
            selectedChargeTypeUnit: null,
        };
        this.onSubmit = this.onSubmit.bind(this);
        this.onAddChargeTypeUnit = this.onAddChargeTypeUnit.bind(this);
        this.onEditChargeTypeUnit = this.onEditChargeTypeUnit.bind(this);
        this.onSaveChargeTypeUnit = this.onSaveChargeTypeUnit.bind(this);
    }

    componentDidMount() {
        this.populateState();
    }

    componentWillUnmount() {
    }

    // #region [ Status Flyout for Add/Edit ]

    // Add New Status
    onAddChargeTypeUnit() {
        const { chargeTypeUnit } = this.state;
        const new_status = new ChargeTypeUnit();
        // This is a new status, embed the Id and description.
        new_status.id = chargeTypeUnit.id ?? 0;
        new_status.description = chargeTypeUnit.description;
        this.setState({
            selectedChargeTypeUnit: new_status,
            showChargeTypeUnitForm: true,
        });
    }

    onClearErrorNotificationClicked = (e) => {
        e.stopPropagation();
        this.setState((state) => ({ errors: {} }));
    };

    // Close status window
    onCloseChargeTypeUnit(response) {
        this.resetForm();
        this.props.onClose(response);
    }

    // Delete status shown
    onDeleteChargeTypeUnit = async (e) => {
        const { chargeTypeUnit } = this.state;
        const response = await util.fetch.delete(ApiRoutes.chargeTypeUnit.delete(chargeTypeUnit.id)).catch(this.onCatchFetchError);
        if (response) this.onClose(response);
    };

    // Status table edit click handler
    onEditChargeTypeUnit(status) {
        this.setState({ selectedChargeTypeUnit: JSON.parse(JSON.stringify(status)), showChargeTypeUnitForm: true });
    }

    onInputChange = (event) => {
        const { chargeTypeUnit } = this.state;
        chargeTypeUnit.description = event.target.value;

        this.setState({
            chargeTypeUnit,
        });
    };

    // After status save callback
    onSaveChargeTypeUnit(status) {
        if (status) {
            this.setState({ showChargeTypeUnitForm: false });
            // refresh the grid
        }
    }

    // #endregion

    async onSubmit() {
    // Clear any fluent api errors
        this.setState((state) => ({ errors: {} }));

        const { chargeTypeUnit } = this.state;

        const url = chargeTypeUnit.id ? ApiRoutes.chargeTypeUnit.update(chargeTypeUnit.id) : ApiRoutes.chargeTypeUnit.create();
        const fetch_stat = chargeTypeUnit.id ? util.fetch.put : util.fetch.post;

        const response = await fetch_stat(url, chargeTypeUnit).catch((err) => {
            if (err && err.validationMessages && (Object.keys(err.validationMessages) ?? []).length) this.setState((state) => ({ errors: err.validationMessages }));
        });

        if (response.ok) {
            const id = await response.json();

            // if we got this far, redirect to employee edit page.
            this.redirect(id);
        }
    }

    async populateState() {
        const chargeTypeUnitId = this.props.match.params.id;

        const data = await Promise.all([
            chargeTypeUnitId ? util.fetch.js(ApiRoutes.chargeTypeUnit.byId(chargeTypeUnitId)) : new ChargeTypeUnit(),
        ]);

        const gridOptions = createGridOptions(this);

        gridOptions.components = {
            statusFilter: DataGridSelectFilter,
            equipmentTypeFilter: DataGridSelectFilter,
        };

        gridOptions.columnDefs = [
            {
                headerName: '', valueGetter: 'node.id', sortable: false, cellRenderer: indexCellRenderer,
            },
            {
                colId: 'ChargeTypeUnit.Description', sortable: true, headerName: 'Description', field: 'description', sort: { direction: 'asc', priority: 0 },
            },
        ];

        const dataSource = createDataSource(ApiRoutes.chargeTypeUnit.search(), gridOptions);

        this.setState((state) => ({
            loading: false,
            chargeTypeUnit: data[0],
            readOnly: false,
            gridOptions,
            dataSource,
        }));
    }

    // #endregion

    redirect(id) {
        this.props.history.push(`${ApplicationPaths.chargeTypeUnit}/${id}`);
    }

    // #region [Render and Render Functions]

    render() {
        if (this.state.loading) return (<LinearProgress variant="indeterminate" color="secondary" />);

        const { rowData, gridOptions } = this.state;

        return (
            <>
                <PageHeading>
                    <FontAwesomeIcon icon={faScrewdriver} className="mr-2 text-muted" />
                    <span>Charge Type Unit</span>
                </PageHeading>
                <Row>
                    <Col>
                        <FlexRow>
                            <FlexStartRow />
                            <FlexEndRow>
                                <Can I="create,view,edit" a="equipment">
                                    <CommonContext.Consumer>
                                        {(value) => (
                                            <SmallButton
                                                type="button"
                                                disabled={!!value.formIsOpen}
                                                hidden={!this.state.selectedChargeTypeUnit}
                                                onClick={this.onEditChargeTypeUnit}
                                            >
                                                <i className="fa fa-edit fa-md mr-2" />
                        View Selected
                                            </SmallButton>
                                        )}
                                    </CommonContext.Consumer>
                                </Can>
                                <Can I="create" a="equipment">
                                    <CommonContext.Consumer>
                                        {(value) => (
                                            <SmallButton
                                                type="button"
                                                disabled={!!value.formIsOpen}
                                                onClick={this.onAddChargeTypeUnit}
                                            >
                                                <i className="fa fa-plus-circle fa-md mr-2" />
                        Add Charge Type Unit
                                            </SmallButton>
                                        )}
                                    </CommonContext.Consumer>
                                </Can>
                            </FlexEndRow>
                        </FlexRow>
                    </Col>
                </Row>
                <DataGrid
                    domLayout="normal"
                    rowData={rowData}
                    gridOptions={gridOptions}
                />
                <StatusForm
                    status={this.state.selectedChargeTypeUnit}
                    show={this.state.showChargeTypeUnitForm}
                    onClose={() => this.setState({ showChargeTypeUnitForm: false })}
                    onSaveCallback={this.onSaveChargeTypeUnit}
                    statusName="Charge Type Unit"
                    route="chargeTypeUnit"
                />
            </>
        );
    }

    // #endregion
}
export default withRouter(ChargeTypeUnitIndex);
